import React, { useState } from "react";
import { toast } from "sonner";
import {
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { BUSINESS, whatsappLink, SERVICE_TIERS } from "@/lib/business";
import { submitQuote } from "@/lib/api";
import SocialLinks from "@/components/SocialLinks";

const VEHICLE_TYPES = ["Standard", "SUV", "Truck"];

const SERVICE_OPTIONS = [
  ...SERVICE_TIERS.map((t) => t.title),
  "Hood Touch-Up",
  "Fender Touch-Up",
  "Single Panel Refinish",
  "Custom / Classic / Body Kit",
  "Other (Describe Below)",
];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_type: "Standard",
  service_type: SERVICE_OPTIONS[0],
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email, and phone.");
      return;
    }

    setSubmitting(true);
    try {
      await submitQuote(form);
      setSuccess(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Could not send your request. Please try WhatsApp or email.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505]">
      <section className="relative pt-16 pb-12 md:pt-24 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00]">
            / CONTACT
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
            REQUEST A
            <br />
            <span className="text-[#E60000]">QUOTE</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#d4d4d8] leading-relaxed">
            Tell us about your vehicle. We'll respond on WhatsApp or email with
            an estimate and next steps.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="lg:col-span-2 bg-[#0a0a0a] p-8 md:p-12"
          >
            {success && (
              <div className="mb-8 border border-[#FFCC00] bg-[#1a1500]/40 p-5 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#FFCC00] mt-0.5" />
                <div>
                  <div className="font-display text-sm tracking-widest text-[#FFCC00]">
                    REQUEST RECEIVED
                  </div>
                  <p className="text-[#e4e4e7] mt-1 text-sm">
                    Thanks — we'll contact you shortly. For urgent requests,{" "}
                    <a
                      href={whatsappLink()}
                      className="underline hover:text-[#FFCC00]"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      message us on WhatsApp
                    </a>
                    .
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Full Name"
                required
                testid="input-full-name"
                value={form.full_name}
                onChange={(v) => update("full_name", v)}
              />
              <Field
                label="Email"
                type="email"
                required
                testid="input-email"
                value={form.email}
                onChange={(v) => update("email", v)}
              />
              <Field
                label="Phone / WhatsApp"
                required
                testid="input-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
              />
              <Field
                label="Vehicle Year"
                testid="input-vehicle-year"
                value={form.vehicle_year}
                onChange={(v) => update("vehicle_year", v)}
                placeholder="e.g. 2018"
              />
              <Field
                label="Vehicle Make"
                testid="input-vehicle-make"
                value={form.vehicle_make}
                onChange={(v) => update("vehicle_make", v)}
                placeholder="e.g. Honda"
              />
              <Field
                label="Vehicle Model"
                testid="input-vehicle-model"
                value={form.vehicle_model}
                onChange={(v) => update("vehicle_model", v)}
                placeholder="e.g. CRV"
              />

              <div>
                <label className="font-display text-xs tracking-[0.3em] text-[#FFCC00] mb-2 block">
                  Vehicle Type
                </label>
                <select
                  value={form.vehicle_type}
                  onChange={(e) => update("vehicle_type", e.target.value)}
                  data-testid="select-vehicle-type"
                  className="w-full bg-[#050505] border border-[#27272A] focus:border-[#E60000] text-white px-4 py-3 outline-none transition-colors font-body"
                >
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#050505]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.3em] text-[#FFCC00] mb-2 block">
                  Service
                </label>
                <select
                  value={form.service_type}
                  onChange={(e) => update("service_type", e.target.value)}
                  data-testid="select-service-type"
                  className="w-full bg-[#050505] border border-[#27272A] focus:border-[#E60000] text-white px-4 py-3 outline-none transition-colors font-body"
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-[#050505]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="font-display text-xs tracking-[0.3em] text-[#FFCC00] mb-2 block">
                Tell us about the work
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="input-message"
                placeholder="Describe damage, color preferences, timeline, or attach photos via WhatsApp..."
                className="w-full bg-[#050505] border border-[#27272A] focus:border-[#E60000] text-white px-4 py-3 outline-none transition-colors font-body resize-none"
              />
            </div>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-quote-btn"
                className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] disabled:opacity-60 disabled:cursor-not-allowed text-white font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> SENDING...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> SEND REQUEST
                  </>
                )}
              </button>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="form-whatsapp-cta"
                className="inline-flex items-center gap-3 border border-[#FFCC00] hover:bg-[#FFCC00] hover:text-black text-[#FFCC00] font-display tracking-widest text-sm px-8 py-4 transition-all"
              >
                <MessageCircle className="w-4 h-4" /> OR WHATSAPP
              </a>
            </div>
            <p className="mt-4 text-xs text-[#71717a]">
              By submitting, you agree to be contacted about your request. No
              spam — just a quote.
            </p>
          </form>

          {/* Info column */}
          <div className="bg-[#050505] p-8 md:p-10">
            <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-6">
              REACH US DIRECTLY
            </div>

            <div className="space-y-6 text-white">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="block border border-[#27272A] hover:border-[#E60000] p-5 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-[#E60000] group-hover:bg-[#FF1A1A] transition-colors">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </span>
                  <div>
                    <div className="font-display text-xs tracking-widest text-[#d4d4d8]">
                      WHATSAPP — FASTEST
                    </div>
                    <div className="font-display text-xl text-white mt-1">
                      {BUSINESS.whatsapp}
                    </div>
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email"
                className="block border border-[#27272A] hover:border-[#FFCC00] p-5 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-[#FFCC00] text-black">
                    <Mail className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="font-display text-xs tracking-widest text-[#d4d4d8]">
                      EMAIL
                    </div>
                    <div className="font-display text-base text-white mt-1 break-all">
                      {BUSINESS.email}
                    </div>
                  </div>
                </div>
              </a>

              <div className="border border-[#27272A] p-5">
                <div className="flex items-start gap-4 mb-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-white text-black">
                    <MapPin className="w-5 h-5" />
                  </span>
                  <div className="flex-1">
                    <div className="font-display text-xs tracking-widest text-[#d4d4d8]">
                      LOCATION
                    </div>
                    <div className="font-display text-lg text-white mt-1">
                      {BUSINESS.location}
                    </div>
                    <div className="text-sm text-[#d4d4d8] mt-1">Cayman Islands</div>
                  </div>
                </div>
                
                {/* Google Map */}
                <div className="relative w-full h-48 mb-4 overflow-hidden border border-[#27272A]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3786.4!2d-81.38!3d19.29!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDE3JzI0LjAiTiA4McKwMjInNDguMCJX!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="The1Autospray Location"
                  ></iframe>
                </div>
                
                {/* Get Directions Button */}
                <a
                  href="https://maps.app.goo.gl/tvPh2MSX5oPNxZgN8"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-btn"
                  className="inline-flex items-center justify-center gap-2 w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-6 py-3 transition-all mb-3"
                >
                  <MapPin className="w-4 h-4" />
                  GET DIRECTIONS
                </a>
                
                {/* Google Business Button */}
                <a
                  href="https://share.google/8loEX332UhuKD4qdm"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-business-btn"
                  className="inline-flex items-center justify-center gap-2 w-full bg-white hover:bg-[#f5f5f5] text-black font-display tracking-widest text-sm px-6 py-3 transition-all"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  FIND US ON GOOGLE
                </a>
              </div>

              <div className="border border-[#27272A] p-5">
                <div className="flex items-start gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-[#27272A] text-white">
                    <Clock className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="font-display text-xs tracking-widest text-[#d4d4d8]">
                      HOURS
                    </div>
                    <div className="font-display text-lg text-white mt-1">
                      {BUSINESS.hours}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-[#27272A] p-5">
                <div className="font-display text-xs tracking-[0.3em] text-[#FFCC00] mb-3">
                  FOLLOW US
                </div>
                <SocialLinks variant="lg" testIdPrefix="contact-social" />
                <p className="mt-3 text-xs text-[#d4d4d8]">
                  See recent builds on Instagram, Facebook, and TikTok.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, testid, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="font-display text-xs tracking-[0.3em] text-[#FFCC00] mb-2 block">
      {label} {required && <span className="text-[#E60000]">*</span>}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-[#050505] border border-[#27272A] focus:border-[#E60000] text-white placeholder-[#52525b] px-4 py-3 outline-none transition-colors font-body"
    />
  </div>
);

export default Contact;
